import { useI18n } from "@/contexts/I18nContext";
import { useBranding } from "@/contexts/BrandingContext";
import InstallButton from "@/components/InstallButton";

/**
 * Minimal credentials-only footer.
 *
 * Shows the auto-rolling copyright + tourism license + commercial register.
 * Live contact actions (phone, chat, map) live on the floating wheel above
 * Fahad (`FloatingContactDock`) so they follow the customer.
 */
export default function GlobalFooter({ variant = "internal" }) {
  const { lang, dir, theme } = useI18n();
  const { branding } = useBranding();
  const year = new Date().getFullYear();

  const copy = lang === "ar"
    ? `© ${year} جميع الحقوق محفوظة لـ VTA`
    : lang === "ur"
      ? `© ${year} جملہ حقوق VTA کے لیے محفوظ ہیں`
      : `© ${year} All rights reserved — VTA`;

  const license = branding.tourism_license_number || "";
  const cr = branding.commercial_register || "";
  const category = lang === "ar"
    ? (branding.license_category_ar || "")
    : (branding.license_category_en || branding.license_category_ar || "");

  const t = lang === "ar"
    ? { license: "تصريح السياحة", cr: "السجل التجاري", category: "الفئة" }
    : lang === "ur"
      ? { license: "سیاحت لائسنس", cr: "تجارتی رجسٹریشن", category: "درجہ" }
      : { license: "Tourism License", cr: "Commercial Register", category: "Category" };

  const css = {
    foot: {
      width: "100%",
      borderTop: `1px solid ${theme.borderSoft}`,
      background: theme.headerBg,
      backdropFilter: "blur(10px)",
      WebkitBackdropFilter: "blur(10px)",
      padding: variant === "portal" ? "20px 24px 24px" : "20px 24px",
      fontFamily: "'Tajawal','Segoe UI',sans-serif",
      marginTop: "32px",
      position: "relative",
      zIndex: 1,
      overflow: "visible",
    },
    inner: {
      maxWidth: "1300px", margin: "0 auto", display: "flex",
      alignItems: "center", gap: "20px", flexWrap: "wrap",
      justifyContent: "space-between",
    },
    left: { flex: "1 1 360px", display: "flex", flexDirection: "column", gap: "6px" },
    copy: { fontSize: "13px", color: theme.text, fontWeight: 700, lineHeight: 1.7 },
    meta: { fontSize: "11.5px", color: theme.textMuted, lineHeight: 1.7, display: "flex", gap: "10px", flexWrap: "wrap" },
    metaPill: {
      display: "inline-flex", alignItems: "center", gap: "6px",
      padding: "3px 12px", borderRadius: "999px",
      border: `1px solid ${theme.borderSoft}`, background: "transparent",
      color: theme.textMuted, fontSize: "11px", fontWeight: 600, whiteSpace: "nowrap",
    },
    label: { color: theme.textMuted, fontSize: "11px", fontWeight: 600 },
    value: { fontWeight: 800, color: theme.text, letterSpacing: "0.3px" },
  };

  return (
    <footer style={css.foot} dir={dir} data-testid="global-footer">
      <div style={css.inner}>
        <div style={css.left}>
          <div style={css.copy} data-testid="footer-copyright">{copy}</div>
          <div style={css.meta} data-testid="footer-credentials">
            {category && (
              <span style={css.metaPill} data-testid="footer-category">
                <span style={css.label}>{t.category}:</span>
                <span style={css.value}>{category}</span>
              </span>
            )}
            {license && (
              <span style={css.metaPill} data-testid="footer-license">
                <span style={css.label}>{t.license}:</span>
                <span style={css.value} dir="ltr">{license}</span>
              </span>
            )}
            {cr && (
              <span style={css.metaPill} data-testid="footer-cr">
                <span style={css.label}>{t.cr}:</span>
                <span style={css.value} dir="ltr">{cr}</span>
              </span>
            )}
          </div>
        </div>
        {variant === "internal" && <InstallButton />}
      </div>
    </footer>
  );
}
